/**
 * Backtest Calculation Utilities
 * 
 * Functions for calculating various backtest metrics from raw data.
 */

/**
 * Calculate drawdown series from equity series
 * @param {Array} equitySeries - Array of { timestamp, equity }
 * @returns {Array} Array of { timestamp, drawdown, drawdownPct }
 */
export const calculateDrawdownSeries = (equitySeries) => {
  if (!equitySeries || equitySeries.length === 0) return [];
  
  let peak = equitySeries[0].equity;
  
  return equitySeries.map(({ timestamp, equity }) => {
    if (equity > peak) {
      peak = equity;
    }
    
    const drawdown = peak - equity;
    const drawdownPct = (drawdown / peak) * 100;
    
    return {
      timestamp,
      drawdown,
      drawdownPct: Math.round(drawdownPct * 100) / 100
    };
  });
};

/**
 * Calculate rolling returns
 * @param {Array} equitySeries - Array of { timestamp, equity }
 * @param {number} window - Rolling window size in days
 * @returns {Array} Array of { timestamp, rollingReturn }
 */
export const calculateRollingReturns = (equitySeries, window = 30) => {
  if (!equitySeries || equitySeries.length < window) return [];
  
  const result = [];
  
  for (let i = window; i < equitySeries.length; i++) {
    const startEquity = equitySeries[i - window].equity;
    const endEquity = equitySeries[i].equity;
    const rollingReturn = ((endEquity - startEquity) / startEquity) * 100;
    
    result.push({
      timestamp: equitySeries[i].timestamp,
      rollingReturn: Math.round(rollingReturn * 100) / 100
    });
  }
  
  return result;
};

/**
 * Calculate rolling Sharpe ratio
 * @param {Array} returnsSeries - Array of { timestamp, return }
 * @param {number} window - Rolling window size in days
 * @param {number} riskFreeRate - Annual risk-free rate (default 0)
 * @returns {Array} Array of { timestamp, rollingSharpe }
 */
export const calculateRollingSharpe = (returnsSeries, window = 30, riskFreeRate = 0) => {
  if (!returnsSeries || returnsSeries.length < window) return [];
  
  const dailyRiskFree = riskFreeRate / 252;
  const result = [];
  
  for (let i = window; i < returnsSeries.length; i++) {
    const windowReturns = returnsSeries.slice(i - window, i).map(r => r.return);
    
    const avgReturn = windowReturns.reduce((a, b) => a + b, 0) / window;
    const variance = windowReturns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / window;
    const stdDev = Math.sqrt(variance);
    
    // Annualized Sharpe
    const sharpe = stdDev > 0 
      ? ((avgReturn - dailyRiskFree) * Math.sqrt(252)) / (stdDev * Math.sqrt(252))
      : 0;
    
    result.push({
      timestamp: returnsSeries[i].timestamp,
      rollingSharpe: Math.round(sharpe * 100) / 100
    });
  }
  
  return result;
};

/**
 * Calculate rolling volatility
 * @param {Array} returnsSeries - Array of { timestamp, return }
 * @param {number} window - Rolling window size in days
 * @returns {Array} Array of { timestamp, rollingVolatility }
 */
export const calculateRollingVolatility = (returnsSeries, window = 30) => {
  if (!returnsSeries || returnsSeries.length < window) return [];
  
  const result = [];
  
  for (let i = window; i < returnsSeries.length; i++) {
    const windowReturns = returnsSeries.slice(i - window, i).map(r => r.return);
    
    const avgReturn = windowReturns.reduce((a, b) => a + b, 0) / window;
    const variance = windowReturns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / window;
    const stdDev = Math.sqrt(variance);
    
    // Annualized volatility
    const annualizedVol = stdDev * Math.sqrt(252) * 100;
    
    result.push({
      timestamp: returnsSeries[i].timestamp,
      rollingVolatility: Math.round(annualizedVol * 100) / 100
    });
  }
  
  return result;
};

/**
 * Aggregate returns by month for heatmap
 * @param {Array} returnsSeries - Array of { timestamp, return }
 * @returns {Array} Array of { year, month, return }
 */
export const aggregateMonthlyReturns = (returnsSeries) => {
  if (!returnsSeries || returnsSeries.length === 0) return [];
  
  const monthlyMap = new Map();
  
  returnsSeries.forEach(({ timestamp, return: ret }) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = date.getMonth(); // 0-11
    const key = `${year}-${month}`;
    
    if (!monthlyMap.has(key)) {
      monthlyMap.set(key, { year, month, returns: [] });
    }
    monthlyMap.get(key).returns.push(ret);
  });
  
  // Calculate cumulative monthly return
  return Array.from(monthlyMap.values()).map(({ year, month, returns }) => {
    // Compound the daily returns
    const monthlyReturn = returns.reduce((acc, r) => acc * (1 + r), 1) - 1;
    
    return {
      year,
      month,
      return: Math.round(monthlyReturn * 10000) / 100 // As percentage
    };
  });
};

/**
 * Calculate trade duration in hours
 * @param {Object} trade - Trade object with entry_time and exit_time
 * @returns {number} Duration in hours
 */
export const calculateTradeDuration = (trade) => {
  const entryTime = new Date(trade.entry_time);
  const exitTime = new Date(trade.exit_time);
  const durationMs = exitTime - entryTime;
  return Math.round(durationMs / (1000 * 60 * 60)); // Hours
};

/**
 * Group trades by duration buckets
 * @param {Array} trades - Array of trade objects
 * @returns {Array} Array of { bucket, count }
 */
export const groupTradesByDuration = (trades) => {
  const buckets = {
    '< 1h': 0,
    '1-4h': 0,
    '4-8h': 0,
    '8-24h': 0,
    '1-3d': 0,
    '> 3d': 0
  };
  
  trades.forEach(trade => {
    const hours = calculateTradeDuration(trade);
    
    if (hours < 1) buckets['< 1h']++;
    else if (hours < 4) buckets['1-4h']++;
    else if (hours < 8) buckets['4-8h']++;
    else if (hours < 24) buckets['8-24h']++;
    else if (hours < 72) buckets['1-3d']++;
    else buckets['> 3d']++;
  });
  
  return Object.entries(buckets).map(([bucket, count]) => ({ bucket, count }));
};

/**
 * Create histogram bins from data
 * @param {Array} values - Array of numeric values
 * @param {number} binCount - Number of bins
 * @returns {Array} Array of { min, max, count, label }
 */
export const createHistogramBins = (values, binCount = 20) => {
  if (!values || values.length === 0) return [];
  
  const min = Math.min(...values);
  const max = Math.max(...values);
  const binWidth = (max - min) / binCount;
  
  const bins = Array(binCount).fill(0).map((_, i) => ({
    min: min + i * binWidth,
    max: min + (i + 1) * binWidth,
    count: 0
  }));
  
  values.forEach(value => {
    const binIndex = Math.min(Math.floor((value - min) / binWidth), binCount - 1);
    if (binIndex >= 0 && binIndex < binCount) {
      bins[binIndex].count++;
    }
  });
  
  return bins.map(bin => ({
    ...bin,
    label: `${bin.min.toFixed(2)} - ${bin.max.toFixed(2)}`
  }));
};

export default {
  calculateDrawdownSeries,
  calculateRollingReturns,
  calculateRollingSharpe,
  calculateRollingVolatility,
  aggregateMonthlyReturns,
  calculateTradeDuration,
  groupTradesByDuration,
  createHistogramBins
};
